import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Calendar, Trash2, CheckCircle, Eye } from 'lucide-react';
import { getApiEndpoint } from '../config/api';

const AdminMessages = () => {
    const [messages, setMessages] = useState([]);
    const [loading, setLoading] = useState(true);
    const [filter, setFilter] = useState('all'); // 'all', 'unread', 'read'
    const [selectedMessage, setSelectedMessage] = useState(null);

    useEffect(() => {
        fetchMessages();
    }, []);

    const fetchMessages = async () => {
        try {
            const response = await axios.get(getApiEndpoint('get_messages.php'));
            setMessages(response.data);
            setLoading(false);
        } catch (error) {
            console.error("Error fetching messages", error);
            setLoading(false);
        }
    };

    const markAsRead = async (id, readStatus = true) => {
        try {
            await axios.put(getApiEndpoint('get_messages.php'), {
                id: id,
                read_status: readStatus ? 1 : 0
            });
            fetchMessages(); // Refresh list
        } catch (error) {
            console.error("Error updating message", error);
        }
    };

    const deleteMessage = async (id) => {
        if (window.confirm("Are you sure you want to delete this message?")) {
            try {
                await axios.delete(getApiEndpoint(`get_messages.php?id=${id}`));
                setSelectedMessage(null);
                fetchMessages(); // Refresh list
            } catch (error) {
                console.error("Error deleting message", error);
                alert("Failed to delete message");
            }
        }
    };

    // Filter messages
    const filteredMessages = messages.filter(message => {
        if (filter === 'all') return true;
        if (filter === 'unread') return !message.read_status;
        if (filter === 'read') return message.read_status;
        return true;
    });

    // Count by status
    const counts = {
        all: messages.length,
        unread: messages.filter(m => !m.read_status).length,
        read: messages.filter(m => m.read_status).length
    };

    if (loading) return <div className="p-8">Loading messages...</div>;

    return (
        <div>
            <div className="admin-header">
                <h2>Contact Messages</h2>
                <div style={{ fontSize: '0.875rem', color: '#64748b' }}>
                    {counts.unread} unread messages
                </div>
            </div>

            {/* Filter Tabs */}
            <div style={{
                display: 'flex',
                gap: '1rem',
                marginBottom: '2rem',
                borderBottom: '1px solid #e2e8f0',
                paddingBottom: '1rem'
            }}>
                {['all', 'unread', 'read'].map(status => (
                    <button
                        key={status}
                        onClick={() => setFilter(status)}
                        style={{
                            padding: '0.5rem 1rem',
                            border: 'none',
                            backgroundColor: filter === status ? '#f1f5f9' : 'transparent',
                            borderRadius: '0.375rem',
                            fontWeight: filter === status ? '600' : '400',
                            cursor: 'pointer',
                            textTransform: 'capitalize'
                        }}
                    >
                        {status} ({counts[status]})
                    </button>
                ))}
            </div>

            {/* Messages List */}
            {filteredMessages.length === 0 ? (
                <div style={{
                    textAlign: 'center',
                    padding: '4rem',
                    color: '#64748b'
                }}>
                    <p>No messages found</p>
                </div>
            ) : (
                <div className="admin-table-container">
                    <table className="admin-table">
                        <thead>
                            <tr>
                                <th style={{ width: '50px' }}>Status</th>
                                <th>Name</th>
                                <th>Email</th>
                                <th>Phone</th>
                                <th>Subject</th>
                                <th>Date</th>
                                <th style={{ textAlign: 'right' }}>Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {filteredMessages.map((message) => (
                                <tr
                                    key={message.id}
                                    style={{
                                        backgroundColor: message.read_status ? 'white' : '#f8fafc',
                                        fontWeight: message.read_status ? 'normal' : '600'
                                    }}
                                >
                                    <td>
                                        <div style={{
                                            width: '10px',
                                            height: '10px',
                                            borderRadius: '50%',
                                            backgroundColor: message.read_status ? '#94a3b8' : '#3b82f6',
                                            margin: '0 auto'
                                        }} />
                                    </td>
                                    <td>{message.name}</td>
                                    <td>
                                        <a
                                            href={`mailto:${message.email}`}
                                            style={{ color: '#3b82f6', textDecoration: 'none' }}
                                        >
                                            {message.email}
                                        </a>
                                    </td>
                                    <td>{message.phone || 'N/A'}</td>
                                    <td>{message.subject || 'No subject'}</td>
                                    <td style={{ fontSize: '0.875rem', color: '#64748b' }}>
                                        {new Date(message.created_at).toLocaleDateString()}
                                    </td>
                                    <td style={{ textAlign: 'right' }}>
                                        <button
                                            onClick={() => {
                                                setSelectedMessage(message);
                                                if (!message.read_status) {
                                                    markAsRead(message.id);
                                                }
                                            }}
                                            className="admin-action-btn"
                                            title="View message"
                                        >
                                            <Eye size={18} />
                                        </button>
                                        {!message.read_status && (
                                            <button
                                                onClick={() => markAsRead(message.id)}
                                                className="admin-action-btn"
                                                title="Mark as read"
                                            >
                                                <CheckCircle size={18} />
                                            </button>
                                        )}
                                        <button
                                            onClick={() => deleteMessage(message.id)}
                                            className="admin-action-btn delete"
                                            title="Delete message"
                                        >
                                            <Trash2 size={18} />
                                        </button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}

            {/* Message Detail Modal */}
            {selectedMessage && (
                <div style={{
                    position: 'fixed',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    backgroundColor: 'rgba(0, 0, 0, 0.7)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    zIndex: 1000,
                    padding: '1rem'
                }}>
                    <div style={{
                        backgroundColor: 'white',
                        borderRadius: '0.5rem',
                        maxWidth: '600px',
                        width: '100%',
                        padding: '2rem',
                        maxHeight: '80vh',
                        overflow: 'auto'
                    }}>
                        <div style={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'start',
                            marginBottom: '1.5rem',
                            paddingBottom: '1rem',
                            borderBottom: '1px solid #e2e8f0'
                        }}>
                            <div>
                                <h3 style={{ fontSize: '1.5rem', marginBottom: '0.5rem' }}>
                                    {selectedMessage.subject || 'No Subject'}
                                </h3>
                                <div style={{ fontSize: '0.875rem', color: '#64748b' }}>
                                    <p style={{ margin: '0.25rem 0' }}>
                                        <strong>From:</strong> {selectedMessage.name} ({selectedMessage.email})
                                    </p>
                                    <p style={{ margin: '0.25rem 0' }}>
                                        <strong>Phone:</strong> {selectedMessage.phone || 'Not provided'}
                                    </p>
                                    <p style={{ margin: '0.25rem 0' }}>
                                        <strong>Received:</strong> {new Date(selectedMessage.created_at).toLocaleString()}
                                    </p>
                                </div>
                            </div>
                            <button
                                onClick={() => setSelectedMessage(null)}
                                style={{
                                    background: 'none',
                                    border: 'none',
                                    fontSize: '1.5rem',
                                    cursor: 'pointer',
                                    color: '#64748b'
                                }}
                            >
                                ×
                            </button>
                        </div>

                        <div style={{
                            backgroundColor: '#f8fafc',
                            padding: '1.5rem',
                            borderRadius: '0.5rem',
                            marginBottom: '1.5rem',
                            lineHeight: '1.6',
                            whiteSpace: 'pre-wrap'
                        }}>
                            {selectedMessage.message}
                        </div>

                        <div style={{ display: 'flex', gap: '1rem', justifyContent: 'flex-end' }}>
                            <button
                                onClick={() => deleteMessage(selectedMessage.id)}
                                className="btn"
                                style={{
                                    backgroundColor: '#ef4444',
                                    borderColor: '#ef4444'
                                }}
                            >
                                <Trash2 size={18} style={{ marginRight: '0.5rem' }} />
                                Delete
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default AdminMessages;
